<template>
  <section
    class="gg-modulo"
    :class="{ 'gg-modulo--offline': offline, 'gg-modulo--collapsed': collapsed }"
    :style="rootStyle"
  >
    <header class="gg-modulo__header">
      <div class="gg-modulo__heading">
        <v-icon v-if="icon" class="gg-modulo__icon" size="20">{{ icon }}</v-icon>
        <h2 class="gg-modulo__title" :title="title">{{ title }}</h2>
      </div>
      <div class="gg-modulo__actions">
        <slot name="actions">
          <button
            v-if="actionIcon"
            type="button"
            class="gg-modulo__action"
            :aria-label="actionLabel"
            :disabled="actionDisabled"
            @click="$emit('action')"
          >
            <v-icon size="20">{{ actionIcon }}</v-icon>
          </button>
        </slot>
        <button
          v-if="collapsible"
          type="button"
          class="gg-modulo__action"
          :aria-label="collapsed ? 'Expandir' : 'Recolher'"
          @click="collapsed = !collapsed"
        >
          <v-icon size="20">{{ collapsed ? 'mdi-unfold-more-horizontal' : 'mdi-unfold-less-horizontal' }}</v-icon>
        </button>
      </div>
    </header>

    <div v-show="!collapsed" class="gg-modulo__body">
      <!-- Offline hides the subheader: its controls (playlist select, search,
           import) all need the site, so they would only fail. -->
      <div v-if="$slots.subheader && !offline" class="gg-modulo__subheader">
        <slot name="subheader" />
      </div>

      <div class="gg-modulo__content">
        <div v-if="offline" class="gg-modulo__placeholder">
          <div class="gg-modulo__placeholder-inner">
            <v-icon class="gg-modulo__placeholder-icon" size="20">mdi-cloud-off-outline</v-icon>
            <span class="gg-modulo__placeholder-text">Sem conexão</span>
          </div>
        </div>
        <slot v-else />
      </div>
    </div>

    <div
      v-if="grip"
      v-show="!collapsed"
      class="gg-modulo__grip"
      role="separator"
      aria-label="Redimensionar bloco"
      @pointerdown="startResize"
    >
      <v-icon size="16">mdi-drag-horizontal-variant</v-icon>
    </div>
  </section>
</template>

<script>
export default {
  name: "GloboGraphicsModulo",
  props: {
    // Header icon — an MDI glyph name (the Figma Material Symbol mapped to MDI).
    icon: { type: String, default: "" },
    title: { type: String, required: true },
    // Header action icon-button (e.g. mdi-open-in-new, mdi-unfold-less). Omit for none.
    actionIcon: { type: String, default: "" },
    actionLabel: { type: String, default: "" },
    actionDisabled: { type: Boolean, default: false },
    // Vizrt-offline state: overlays the content region with "Sem conexão".
    offline: { type: Boolean, default: false },
    // Drag-reorder grip footer.
    grip: { type: Boolean, default: true },
    // Header chevron that collapses the body to just the header row.
    collapsible: { type: Boolean, default: false },
  },
  data() {
    return { collapsed: false, resizeHeight: null };
  },
  computed: {
    // When the operator has dragged the grip, pin the module to that explicit
    // height (growing the page rather than stealing from siblings). Collapsed wins
    // — the class handles it.
    rootStyle() {
      if (this.collapsed || this.resizeHeight == null) return {};
      return { height: `${this.resizeHeight}px` };
    },
  },
  methods: {
    // Grip = a vertical resize handle: drag it to grow the block (and the page).
    startResize(e) {
      const startY = e.clientY;
      const startH = this.$el.getBoundingClientRect().height;
      const prevUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = "none";
      const onMove = (ev) => {
        this.resizeHeight = Math.max(120, startH + (ev.clientY - startY));
      };
      const onUp = () => {
        document.body.style.userSelect = prevUserSelect;
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-modulo {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: var(--sc-gg-surface-2);
  border-radius: var(--sc-radius-xl);
}

// Collapsed = only the header row (height shrinks to fit; overrides any set height).
.gg-modulo--collapsed {
  height: auto !important;
  flex: 0 0 auto !important;
}

// Fixed 52px header (per the design) — the action/collapse icon buttons are 32px
// tall, so vertical padding here would grow the header to 64px and, in a short
// module (Preview at 200px), overflow the content past the grip. A fixed height
// with horizontal-only padding keeps every module's header identical and lets the
// buttons centre inside it.
.gg-modulo__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
  flex-shrink: 0;
  height: 52px;
  padding: 0 var(--sc-space-4);
}

.gg-modulo__heading {
  display: flex;
  align-items: center;
  gap: var(--sc-space-4);
  min-width: 0;
}

.gg-modulo__icon {
  color: var(--sc-gg-text-strong) !important;
}

.gg-modulo__title {
  margin: 0;
  font-size: 16px;
  font-weight: var(--sc-weight-semi);
  line-height: 20px;
  color: var(--sc-gg-text-title);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gg-modulo__actions {
  display: flex;
  align-items: center;
  gap: var(--sc-space-1);
  flex-shrink: 0;
}

.gg-modulo__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--sc-radius-md);
  color: var(--sc-gg-text-strong);
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  .v-icon {
    color: inherit !important;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.06);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.gg-modulo__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  border-top: 1px solid var(--sc-gg-border-100);
}

.gg-modulo__subheader {
  padding: var(--sc-space-4);
  border-bottom: 1px solid var(--sc-gg-border-100);
}

.gg-modulo__content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 132px;
  padding: var(--sc-space-4);
  overflow-y: auto;

  // Visible thin scrollbar (the app hides scrollbars globally; the module scrolls
  // internally so it needs one back).
  scrollbar-width: thin;
  scrollbar-color: var(--sc-gg-border-200) transparent;

  &::-webkit-scrollbar {
    display: block;
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--sc-gg-border-200);
    border-radius: var(--sc-radius-pill);
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

// Offline placeholder — the dark image-overlay box with cloud_off + "Sem conexão".
// Shares the same box treatment the empty preview/editor states use.
.gg-modulo__placeholder {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  border-radius: var(--sc-radius-sm);
  background: var(--sc-gg-overlay-image);
}

.gg-modulo__placeholder-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sc-space-2);
}

.gg-modulo__placeholder-icon {
  color: var(--sc-gg-text-tertiary) !important;
}

.gg-modulo__placeholder-text {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.07px;
  color: var(--sc-gg-text-tertiary);
}

.gg-modulo__grip {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  flex-shrink: 0;
  border-top: 1px solid var(--sc-gg-border-100);
  cursor: ns-resize;
  touch-action: none;
  user-select: none;

  .v-icon {
    color: var(--sc-gg-text-strong) !important;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
}
</style>
